import { useMemo } from 'react';

import flatMap from 'lodash/flatMap';

import { SECONDS_IN_DAY } from '@cast/constants';
import { useDebounce } from '@cast/utils';

import { mapWorkloadDataTransfer } from 'features/cluster/cost-report/workloads/network-cost/providers/_utils';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { usePaginatedDataTransferCostQuery } from 'hooks/queries/cost';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { UseServerData } from '@cast/shared-search-ui';

export const useReducedWorkloads: UseServerData = ({
  values,
  sortingState,
}) => {
  const { cluster } = useCluster();
  const { labels, namespaces, workloadNames, workloadTypes } =
    useDebounce(values);

  const {
    adjustedDateRange,
    isEgressdInstalled,
    isKvisorInstalled,
    isCapabilitiesLoading,
  } = useCostReportContext();

  const {
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
    count,
    isFetching,
  } = usePaginatedDataTransferCostQuery({
    clusterId: cluster.id,
    startTime: adjustedDateRange[0].toISOString(),
    endTime: adjustedDateRange[1].toISOString(),
    stepSeconds: SECONDS_IN_DAY,
    sortBy: sortingState?.columnId,
    sortDirection: sortingState?.sortBy.direction,
    enabled: !!cluster.id && (isEgressdInstalled || isKvisorInstalled),
    labels,
    namespaces,
    workloadNames,
    workloadTypes,
  });

  const normalizedData = useMemo(() => {
    return mapWorkloadDataTransfer(
      flatMap(data?.map((item) => item.items || []) || [])
    );
  }, [data]);

  return {
    data: normalizedData,
    isLoading: isLoading || isCapabilitiesLoading,
    hasNextPage: !isLoading && hasNextPage,
    fetchNextPage,
    hasPreviousPage: !isLoading,
    error,
    refetch,
    metadata: count || 0,
    isFetching,
  };
};
